import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import courseContent, { weeks } from '../content';
import useLocalStorage from '../hooks/useLocalStorage';

function LearnerTrack() {
  const { week: weekParam } = useParams();
  const [currentWeek, setCurrentWeek] = useState(parseInt(weekParam) || 1);
  const [currentModuleIndex, setCurrentModuleIndex] = useState(0);
  const [reflections, setReflections] = useLocalStorage('nma-reflections', {});
  const [saveStatus, setSaveStatus] = useState('');

  useEffect(() => {
    if (weekParam) {
      setCurrentWeek(parseInt(weekParam));
      setCurrentModuleIndex(0);
    }
  }, [weekParam]);

  const weekData = courseContent[currentWeek];
  const currentModule = weekData?.modules[currentModuleIndex];
  
  const reflectionKey = `week${currentWeek}`;
  const currentReflection = reflections[reflectionKey] || '';

  const handleReflectionChange = (e) => {
    const newReflections = {
      ...reflections,
      [reflectionKey]: e.target.value
    };
    setReflections(newReflections);
    setSaveStatus('Saving...');
    setTimeout(() => setSaveStatus('Saved'), 500);
  };

  const downloadReflections = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 20;
    const maxWidth = pageWidth - (margin * 2);
    let yPosition = 20;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(18);
    doc.text('My Navigational Mind Reflections', margin, yPosition);
    yPosition += 15;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    doc.text(`Downloaded: ${new Date().toLocaleDateString()}`, margin, yPosition);
    yPosition += 20;

    weeks.forEach((week) => {
      const key = `week${week.number}`;
      const reflection = reflections[key];
      
      if (reflection && reflection.trim()) {
        // Check if we need a new page
        if (yPosition > 250) {
          doc.addPage();
          yPosition = 20;
        }

        doc.setFont('helvetica', 'bold');
        doc.setFontSize(12);
        doc.text(`Week ${week.number}: ${week.title}`, margin, yPosition);
        yPosition += 8;

        doc.setFont('helvetica', 'italic');
        doc.setFontSize(10);
        const promptLines = doc.splitTextToSize(week.reflection.prompt, maxWidth);
        doc.text(promptLines, margin, yPosition);
        yPosition += promptLines.length * 5 + 5;

        doc.setFont('helvetica', 'normal');
        doc.setFontSize(10);
        const reflectionLines = doc.splitTextToSize(reflection, maxWidth);
        doc.text(reflectionLines, margin, yPosition);
        yPosition += reflectionLines.length * 5 + 15;
      }
    });

    doc.save('navigational-mind-reflections.pdf');
  };

  const goToNextModule = () => {
    if (currentModuleIndex < weekData.modules.length - 1) {
      setCurrentModuleIndex(currentModuleIndex + 1);
      window.scrollTo(0, 0);
    }
  };

  const goToPrevModule = () => {
    if (currentModuleIndex > 0) {
      setCurrentModuleIndex(currentModuleIndex - 1);
      window.scrollTo(0, 0);
    }
  };

  const isLastModule = currentModuleIndex === weekData.modules.length - 1;

  return (
    <div className="fade-in">
      {/* Week Navigation */}
      <nav className="week-nav">
        <div className="week-nav-inner">
          {weeks.map((week) => (
            <Link
              key={week.number}
              to={`/learner/${week.number}`}
              className={`week-tab ${currentWeek === week.number ? 'active' : ''}`}
              onClick={() => {
                setCurrentWeek(week.number);
                setCurrentModuleIndex(0);
              }}
            >
              Week {week.number}
            </Link>
          ))}
        </div>
      </nav>

      <div className="container" style={{ marginTop: 'var(--space-xl)' }}>
        {/* Week Header */}
        <div className="week-header">
          <h2>Week {currentWeek}: {weekData.title}</h2>
          <p className="week-theme">{weekData.theme}</p>
          <div className="week-progress">
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${((currentModuleIndex + 1) / weekData.modules.length) * 100}%` }}
              />
            </div>
            <span className="progress-text">
              {currentModuleIndex + 1} of {weekData.modules.length} modules
            </span>
          </div>
        </div>

        {/* Module List */}
        <div className="module-list">
          {weekData.modules.map((module, index) => (
            <div
              key={module.id}
              className={`module-item ${index === currentModuleIndex ? 'active' : ''}`}
              onClick={() => setCurrentModuleIndex(index)}
            >
              <span className="module-number">{module.id}</span>
              <span className="module-title">{module.title}</span>
              {index < currentModuleIndex && (
                <span className="module-check">✓</span>
              )}
            </div>
          ))}
        </div>

        {/* Current Module Content */}
        <div className="module-content">
          <div className="module-header">
            <span className="module-label">Module {currentModule.id}</span>
            <h3>{currentModule.title}</h3>
          </div>
          <div className="module-body">
            {currentModule.content.split('\n\n').map((paragraph, idx) => {
              // Handle headers marked with **
              if (paragraph.startsWith('**') && paragraph.endsWith('**')) {
                return <h4 key={idx}>{paragraph.slice(2, -2)}</h4>;
              }
              // Handle paragraphs with bold text
              const parts = paragraph.split(/(\*\*.*?\*\*)/g);
              return (
                <p key={idx}>
                  {parts.map((part, partIdx) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={partIdx}>{part.slice(2, -2)}</strong>;
                    }
                    return part;
                  })}
                </p>
              );
            })}
          </div>

          {/* Navigation between modules */}
          <div className="module-nav">
            <button 
              className="module-nav-btn" 
              onClick={goToPrevModule}
              disabled={currentModuleIndex === 0}
            >
              ← Previous
            </button>
            <button 
              className="module-nav-btn" 
              onClick={goToNextModule}
              disabled={isLastModule}
            >
              Next →
            </button>
          </div>
        </div>

        {/* Reflection Area - shown after last module */}
        {isLastModule && (
          <div className="reflection-area">
            <div className="reflection-header">
              <h3 className="reflection-title">Week {currentWeek} Reflection</h3>
            </div>
            <p className="reflection-prompt">
              {weekData.reflection.prompt}
            </p>
            <textarea
              className="reflection-textarea"
              value={currentReflection}
              onChange={handleReflectionChange}
              placeholder={weekData.reflection.placeholder}
            />
            <div className="reflection-actions">
              <button className="btn btn-secondary" onClick={downloadReflections}>
                Download All Reflections
              </button>
              {saveStatus && <span className="save-indicator">{saveStatus}</span>}
            </div>
          </div>
        )}

        {/* AI Tutor Prompt */}
        <div className="tutor-prompt">
          <p>
            Want to explore this further or apply it to your own situation?
          </p>
          <Link to="/tutor" className="btn">
            ✦ Open AI Tutor
          </Link>
        </div>
      </div>
    </div>
  );
}

export default LearnerTrack;
